'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@e6/ds/core';
import { BookOpen, LogOut, Shield, UserCog, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface User {
  name: string;
  email: string;
  avatarUrl?: string;
  initials: string;
}

interface UserMenuProps {
  user?: User;
  isLoading?: boolean;
  isAuthenticated?: boolean;
  onPrivacyClick?: () => void;
  onDocumentationClick?: () => void;
  onLogout?: () => void;
}

export function UserMenu({
  user,
  isLoading = false,
  isAuthenticated = false,
  onPrivacyClick,
  onDocumentationClick,
  onLogout,
}: UserMenuProps) {
  const navigate = useNavigate();
  // Loading state
  if (isLoading) {
    return (
      <div className="bg-muted h-9 w-9 animate-pulse rounded-full" aria-label="Loading user" />
    );
  }

  // Not authenticated - show anonymous avatar
  if (!isAuthenticated || !user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="focus-visible:ring-ring flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2"
            aria-label="User menu"
          >
            <Avatar className="h-9 w-9 cursor-pointer">
              <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">
                <UserIcon className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" sideOffset={8}>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">Not signed in</p>
              <p className="text-muted-foreground text-xs leading-none">
                Running in local development mode
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={onDocumentationClick}>
              <BookOpen className="mr-2 h-4 w-4" />
              <span>Documentation</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPrivacyClick}>
              <Shield className="mr-2 h-4 w-4" />
              <span>Privacy policy</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Authenticated user
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="focus-visible:ring-ring flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2"
          aria-label="User menu"
        >
          <Avatar className="h-9 w-9 cursor-pointer">
            {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
              {user.initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" sideOffset={8}>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-muted-foreground text-xs leading-none">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate('/user-settings')}>
            <UserCog className="mr-2 h-4 w-4" />
            <span>User settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDocumentationClick}>
            <BookOpen className="mr-2 h-4 w-4" />
            <span>Documentation</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onPrivacyClick}>
            <Shield className="mr-2 h-4 w-4" />
            <span>Privacy policy</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
